import React from "react";
import { createRoot } from "react-dom/client";
import { QRCodeCanvas } from "qrcode.react";

// The Edu-Action logo, as used by the payment print view. (black.svg in the
// same folder is the admin theme's placeholder branding, not ours.)
import logoUrl from "assets/logos/edu.png";

/**
 * Renders an electronic receipt to a 72 mm thermal bitmap and sends it to the
 * bridge for printing.
 *
 * It is drawn to a canvas and printed as a picture rather than as text: the
 * printer never has to interpret a codepage, so Latin, Cyrillic and the logo
 * all come out exactly as designed.
 *
 * 72 mm at 203 dpi is 576 dots, which is also the printable width of an 80 mm
 * roll and a whole number of bytes per row.
 */
export const PRINT_WIDTH = 576;

const PAD = 16;
const LEFT = PAD;
const RIGHT = PRINT_WIDTH - PAD;
const INNER = RIGHT - LEFT;

const FONT = (size, weight = "normal") =>
  `${weight} ${size}px "Helvetica Neue", Arial, sans-serif`;

const money = (value, currency = "UZS") =>
  `${Math.round(Number(value) || 0)
    .toString()
    .replace(/\B(?=(\d{3})+(?!\d))/g, " ")}${currency ? ` ${currency}` : ""}`;

/** Draws an image offscreen and resolves once it is decoded. */
const loadImage = (src) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`could not load ${src}`));
    img.src = src;
  });

const LOGO_WIDTH = 220;

// Brand colours are far too light for a thermal printer's black/white cut:
// Edu-Action orange sits on the white side and would print as blank paper. The
// logo is reduced to a silhouette first - anything with real ink becomes black,
// the paper stays white - so it prints regardless of its colours.
const LOGO_INK_CUTOFF = 235;

const toSilhouette = (img, width) => {
  const height = Math.round((img.height * width) / img.width);
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  // over white paper, so transparent logos behave like opaque ones
  ctx.fillStyle = "#fff";
  ctx.fillRect(0, 0, width, height);
  ctx.drawImage(img, 0, 0, width, height);
  const frame = ctx.getImageData(0, 0, width, height);
  const px = frame.data;
  for (let i = 0; i < px.length; i += 4) {
    const luma = 0.299 * px[i] + 0.587 * px[i + 1] + 0.114 * px[i + 2];
    const value = luma < LOGO_INK_CUTOFF ? 0 : 255;
    px[i] = value;
    px[i + 1] = value;
    px[i + 2] = value;
    px[i + 3] = 255;
  }
  ctx.putImageData(frame, 0, 0);
  return canvas;
};

/** Renders a QR offscreen with the library the panel already ships. */
const renderQr = (value, size) =>
  new Promise((resolve) => {
    const holder = document.createElement("div");
    holder.style.cssText = "position:fixed;left:-10000px;top:0";
    document.body.appendChild(holder);
    const root = createRoot(holder);
    root.render(
      React.createElement(QRCodeCanvas, {
        value,
        size,
        level: "M",
        includeMargin: false,
      }),
    );
    // let React paint before reading the canvas back
    setTimeout(() => {
      const canvas = holder.querySelector("canvas");
      const dataUrl = canvas ? canvas.toDataURL() : null;
      root.unmount();
      holder.remove();
      resolve(dataUrl);
    }, 60);
  });

/** The OFD receipt number, which lives only inside the fiscal QR link. */
const ofdReceiptNumber = (qr) => {
  const match = /[?&]r=([^&]+)/.exec(`${qr ?? ""}`);
  return match ? decodeURIComponent(match[1]).trim() : "";
};

/** Simple vertical layout helper over a canvas context. */
class Sheet {
  constructor(ctx) {
    this.ctx = ctx;
    this.y = PAD;
  }

  gap(px = 8) {
    this.y += px;
  }

  line(dashed = false) {
    const { ctx } = this;
    ctx.save();
    ctx.strokeStyle = "#000";
    ctx.lineWidth = 2;
    if (dashed) ctx.setLineDash([6, 6]);
    ctx.beginPath();
    ctx.moveTo(LEFT, this.y);
    ctx.lineTo(RIGHT, this.y);
    ctx.stroke();
    ctx.restore();
    this.y += 12;
  }

  text(value, { size = 22, weight = "normal", align = "left" } = {}) {
    const { ctx } = this;
    ctx.font = FONT(size, weight);
    ctx.fillStyle = "#000";
    ctx.textBaseline = "top";
    const lines = this.wrap(String(value ?? ""), INNER);
    for (const lineText of lines) {
      const width = ctx.measureText(lineText).width;
      const x =
        align === "center"
          ? LEFT + (INNER - width) / 2
          : align === "right"
            ? RIGHT - width
            : LEFT;
      ctx.fillText(lineText, x, this.y);
      this.y += size + 6;
    }
  }

  /** A label on the left and a value on the right, on one baseline. */
  row(label, value, { size = 22, weight = "normal" } = {}) {
    const { ctx } = this;
    ctx.font = FONT(size, weight);
    ctx.fillStyle = "#000";
    ctx.textBaseline = "top";
    const right = String(value ?? "");
    const rightWidth = ctx.measureText(right).width;
    const labelLines = this.wrap(String(label ?? ""), INNER - rightWidth - 12);
    ctx.fillText(labelLines[0] || "", LEFT, this.y);
    ctx.fillText(right, RIGHT - rightWidth, this.y);
    this.y += size + 6;
    for (const extra of labelLines.slice(1)) {
      ctx.fillText(extra, LEFT, this.y);
      this.y += size + 6;
    }
  }

  wrap(value, maxWidth) {
    const { ctx } = this;
    const words = value.split(/\s+/).filter(Boolean);
    if (!words.length) return [""];
    const lines = [];
    let current = words[0];
    for (const word of words.slice(1)) {
      const candidate = `${current} ${word}`;
      if (ctx.measureText(candidate).width <= maxWidth) current = candidate;
      else {
        lines.push(current);
        current = word;
      }
    }
    lines.push(current);
    return lines;
  }

  image(img, width) {
    const scale = width / img.width;
    const height = img.height * scale;
    this.ctx.drawImage(img, LEFT + (INNER - width) / 2, this.y, width, height);
    this.y += height + 10;
  }
}

/** Draws the receipt and returns the canvas. */
export const renderReceipt = async (receipt) => {
  const canvas = document.createElement("canvas");
  canvas.width = PRINT_WIDTH;
  // generous first pass; the sheet is cropped to its real height afterwards
  canvas.height = 4000;
  const ctx = canvas.getContext("2d");
  ctx.fillStyle = "#fff";
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  const sheet = new Sheet(ctx);

  // --- logo ---
  try {
    const logo = await loadImage(logoUrl);
    if (logo.width && logo.height) {
      sheet.image(toSilhouette(logo, LOGO_WIDTH), LOGO_WIDTH);
    }
  } catch {
    /* a missing logo must not stop a receipt printing */
  }

  // --- company ---
  const m = receipt.merchant || {};
  if (m.name) sheet.text(m.name, { size: 26, weight: "bold", align: "center" });
  if (m.address) sheet.text(m.address, { size: 20, align: "center" });
  if (m.tin) sheet.text(`STIR: ${m.tin}`, { size: 20, align: "center" });
  if (m.terminalSerial)
    sheet.text(`S/N: ${m.terminalSerial}`, { size: 20, align: "center" });
  sheet.gap(4);
  if (receipt.cashier) sheet.row("KASSIR:", receipt.cashier, { size: 20 });
  // Receipts formed before the OFD number was stored carry the terminal's sale
  // id in `receiptNo`, so the QR is preferred whenever it is there.
  const receiptNo =
    ofdReceiptNumber(receipt.fiscal?.qr) || receipt.receiptNo || "-";
  sheet.row(
    `CHEK N ${receiptNo}`,
    [receipt.date, receipt.time].filter(Boolean).join(" "),
    { size: 20 },
  );
  sheet.line();

  // --- candidate and exam ---
  const c = receipt.candidate || {};
  const e = receipt.exam || {};
  if (c.fullName) sheet.row("Nomzod:", c.fullName, { size: 20 });
  if (c.passportId) sheet.row("Pasport:", c.passportId, { size: 20 });
  if (c.pinpp) sheet.row("JSHSHIR:", c.pinpp, { size: 20 });
  if (e.exam) sheet.row("Imtihon:", e.exam, { size: 20 });
  if (e.module) sheet.row("Modul:", e.module, { size: 20 });
  if (e.examDate) sheet.row("Imtihon sanasi:", e.examDate, { size: 20 });
  if (e.enquiryId) sheet.row("Ariza:", e.enquiryId, { size: 20 });
  sheet.line();

  // --- lines ---
  for (const item of receipt.lines || []) {
    sheet.text(item.name, { size: 22, weight: "bold" });
    sheet.row(
      `${item.quantity} x ${money(item.price, "")}`,
      money(item.total, ""),
      { size: 20 },
    );
    if (item.vatPercent)
      sheet.text(
        `Shu jumladan QQS ${item.vatPercent}%: ${money(item.vat, "")}`,
        {
          size: 18,
        },
      );
    if (item.mxik) sheet.text(`MXIK: ${item.mxik}`, { size: 18 });
    sheet.gap(4);
  }
  sheet.line();

  // --- totals ---
  const t = receipt.totals || {};
  if (t.discount) sheet.row("Chegirma:", money(t.discount, ""), { size: 20 });
  if (receipt.promocode?.code)
    sheet.row("Promokod:", receipt.promocode.code, { size: 20 });
  sheet.row("TO'LOV UCHUN:", money(t.total, ""), { size: 26, weight: "bold" });
  if (t.vat) sheet.row("Shu jumladan QQS:", money(t.vat, ""), { size: 20 });
  for (const tender of receipt.tenders || []) {
    sheet.row(`To'lov shakli — ${tender.method}:`, money(tender.amount, ""), {
      size: 20,
    });
    if (tender.otherTerminal) {
      sheet.text(
        `Boshqa terminalda to'langan${
          tender.externalReference ? `, ref: ${tender.externalReference}` : ""
        }`,
        { size: 18 },
      );
    }
  }

  // --- card slip ---
  if (receipt.card?.cardMask) {
    sheet.line(true);
    sheet.row("Karta:", receipt.card.cardMask, { size: 20 });
    if (receipt.card.cardType)
      sheet.row("Turi:", receipt.card.cardType, { size: 20 });
    if (receipt.card.rrn) sheet.row("RRN:", receipt.card.rrn, { size: 20 });
    if (receipt.card.authCode)
      sheet.row("Auth:", receipt.card.authCode, { size: 20 });
  }

  // --- fiscal ---
  const f = receipt.fiscal || {};
  sheet.line();
  sheet.text("GOOD LUCK WITH THE EXAM", {
    size: 22,
    weight: "bold",
    align: "center",
  });
  sheet.gap(4);
  sheet.line();
  sheet.text("TAKE THE TEST", { size: 24, weight: "bold", align: "center" });
  sheet.text("WITH THE BEST", { size: 24, weight: "bold", align: "center" });
  sheet.gap(4);
  if (f.fiscalSign) sheet.row("FB:", f.fiscalSign, { size: 20 });
  if (f.fiscalModule) sheet.row("FM:", f.fiscalModule, { size: 20 });
  if (f.qr) {
    sheet.gap(6);
    sheet.text("Soliq QR kodi", { size: 20, align: "center" });
    const qrDataUrl = await renderQr(f.qr, 260);
    let qrDrawn = false;
    if (qrDataUrl) {
      try {
        sheet.image(await loadImage(qrDataUrl), 260);
        qrDrawn = true;
      } catch {
        /* fall through to the printed URL below */
      }
    }
    // The URL is only worth the paper when the code itself could not be drawn:
    // without it there would be no way to reach the receipt on ofd.soliq.uz.
    if (!qrDrawn) sheet.text(f.qr, { size: 14, align: "center" });
  }
  if (f.cashback) sheet.text(f.cashback, { size: 18, align: "center" });
  sheet.gap(24);

  // crop to what was actually drawn
  const height = Math.min(Math.ceil(sheet.y), canvas.height);
  const cropped = document.createElement("canvas");
  cropped.width = PRINT_WIDTH;
  cropped.height = height;
  const cropCtx = cropped.getContext("2d");
  cropCtx.fillStyle = "#fff";
  cropCtx.fillRect(0, 0, PRINT_WIDTH, height);
  cropCtx.drawImage(canvas, 0, 0);
  return cropped;
};

/**
 * Packs a canvas into the 1-bit, row-major bitmap ESC/POS expects: one bit per
 * dot, most significant bit leftmost, 1 = black.
 */
export const packCanvas = (canvas) => {
  const { width, height } = canvas;
  const { data } = canvas.getContext("2d").getImageData(0, 0, width, height);
  const bytesPerRow = Math.ceil(width / 8);
  const packed = new Uint8Array(bytesPerRow * height);

  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      const i = (y * width + x) * 4;
      // luminance, with transparent treated as white paper
      const alpha = data[i + 3] / 255;
      const luma =
        (0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2]) * alpha +
        255 * (1 - alpha);
      if (luma < 128) {
        packed[y * bytesPerRow + (x >> 3)] |= 0x80 >> (x & 7);
      }
    }
  }

  let binary = "";
  for (const byte of packed) binary += String.fromCharCode(byte);
  return { width, height, data: window.btoa(binary) };
};

export const buildPrintPayload = async (receipt) =>
  packCanvas(await renderReceipt(receipt));
